import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  Table,
  Statistic,
  Spin,
  Tag,
  Button,
  Progress,
  Empty,
  Tooltip,
  Select,
  Space,
  Popconfirm,
  message,
  Result,
  Modal,
} from "antd";
import {
  FileTextOutlined,
  EyeOutlined,
  BankOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  InboxOutlined,
  ExclamationCircleOutlined,
  EnvironmentOutlined,
  InboxOutlined as ArchiveOutlined,
  SafetyCertificateOutlined,
  CarOutlined,
  GiftOutlined,
  GlobalOutlined,
  StopOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import { useBookings } from "../hooks/useBookings";
import { useTariffs, Tariff } from "../hooks/useTariffs";
import { useCompany } from "../hooks/useCompany";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabase";

// Status colors and icons
const statusColors: Record<string, string> = {
  concept: "#faad14", // Yellow - draft
  submitted: "#1890ff", // Blue - in process
  approved: "#52c41a", // Green - approved
  rejected: "#f5222d", // Red - rejected
  waiting_for_goods: "#36cfc9", // Cyan - waiting
  goods_received_origin: "#722ed1", // Purple - goods received
  custom_clearance: "#722ed1", // Purple - customs
  out_for_delivery: "#13c2c2", // Cyan - delivery
  delivered: "#389e0d", // Green - delivered
  added_to_stock: "#73d13d", // Light green - added to stock
  in_transit: "#096dd9", // Blue - transit
  cancelled: "#ff4d4f", // Red - cancelled
  archived: "#8c8c8c", // Grey - archived
};

const statusIcons: Record<string, React.ReactNode> = {
  concept: <FileTextOutlined style={{ fontSize: 20 }} />,
  submitted: <ClockCircleOutlined style={{ fontSize: 20 }} />,
  approved: <CheckCircleOutlined style={{ fontSize: 20 }} />,
  rejected: <ExclamationCircleOutlined style={{ fontSize: 20 }} />,
  waiting_for_goods: <InboxOutlined style={{ fontSize: 20 }} />,
  goods_received_origin: <InboxOutlined style={{ fontSize: 20 }} />,
  custom_clearance: <SafetyCertificateOutlined style={{ fontSize: 20 }} />,
  out_for_delivery: <CarOutlined style={{ fontSize: 20 }} />,
  delivered: <GiftOutlined style={{ fontSize: 20 }} />,
  added_to_stock: (
    <InboxOutlined
      style={{ fontSize: 20, transform: "scale(1.1)", color: "#73d13d" }}
    />
  ),
  in_transit: <GlobalOutlined style={{ fontSize: 20 }} />,
  cancelled: <StopOutlined style={{ fontSize: 20 }} />,
  archived: <DeleteOutlined style={{ fontSize: 20 }} />,
};

const DashboardContainer = styled.div`
  padding: 24px;
`;

const StatsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;

  .stats-section {
    display: grid;
    gap: 16px;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  }

  .divider {
    height: 1px;
    background-color: #f0f0f0;
    margin: 8px 0;
  }

  @media (max-width: 768px) {
    .stats-section:last-child > *:nth-of-type(n + 4) {
      display: none;
    }

    &.expanded .stats-section:last-child > * {
      display: block;
    }
  }
`;

interface StyledCardProps {
  isStatusCard?: boolean;
}

const StyledCard = styled(Card)<StyledCardProps>`
  border-radius: 8px;
  cursor: ${(props) => (props.isStatusCard ? "pointer" : "default")};
  transition: all 0.3s;

  ${(props) =>
    props.isStatusCard &&
    `
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
  `}

  .ant-statistic-title {
    font-size: 14px;
    margin-bottom: 8px;
  }

  .ant-statistic-content {
    font-size: 24px;
  }
`;

const RouteSelect = styled(Select)`
  width: 100%;
  margin-bottom: 16px;
`;

interface ValidityTagProps {
  isActive: boolean;
}

const ValidityTag = styled(Tag)<ValidityTagProps>`
  margin-right: 0;
  background-color: ${(props) => (props.isActive ? "#f6ffed" : "#fff7e6")};
  border-color: ${(props) => (props.isActive ? "#b7eb8f" : "#ffd591")};
  color: ${(props) => (props.isActive ? "#52c41a" : "#fa8c16")};
`;

interface TariffDisplayItem {
  key: number;
  price: string;
  shipmentType: string;
  validity: React.ReactNode;
  isActive: boolean;
  isFuture: boolean;
  fromDate: number;
}

// Add these styled components near the top with the other styled components
const DesktopView = styled.div`
  display: none;
  @media (min-width: 1200px) {
    display: block;
  }
`;

const MobileView = styled.div`
  display: block;
  @media (min-width: 1200px) {
    display: none;
  }
`;

const TariffCard = styled(Card)`
  height: 100%;
  .ant-card-head-title {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

interface StatusBooking {
  id: number;
  reference: string;
  transport_type: string;
  eta: string;
  isEditable: boolean;
}

const ShowMoreButton = styled(Button)`
  width: 100%;
  @media (min-width: 769px) {
    display: none;
  }
`;

export const Dashboard = () => {
  const navigate = useNavigate();
  const { selectedCompany, companyDetails } = useCompany();
  const { t } = useTranslation("translation");
  const [hasCompany, setHasCompany] = useState<boolean | null>(null);
  const [selectedAirRoute, setSelectedAirRoute] = useState<string | null>(null);
  const [selectedOceanRoute, setSelectedOceanRoute] = useState<string | null>(
    null
  );
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Only load dashboard data if user has a company
  const {
    bookings,
    loading: loadingBookings,
    archiveBooking,
  } = useBookings(hasCompany ? selectedCompany : null, true);
  const { tariffs, loading: loadingTariffs } = useTariffs();

  useEffect(() => {
    const checkUserCompany = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) return;

        const { data: userCompanies } = await supabase
          .from("user_companies")
          .select("company_id")
          .eq("user_id", user.id);

        setHasCompany(userCompanies && userCompanies.length > 0);
      } catch (error) {
        console.error("Error checking user company:", error);
        setHasCompany(false);
      }
    };

    checkUserCompany();
  }, []);

  // Filter out expired tariffs (end date is before today)
  const today = dayjs().startOf("day");
  const validTariffs =
    tariffs?.filter(
      (t) => !t.to || dayjs(t.to).isAfter(today) || dayjs(t.to).isSame(today)
    ) || [];

  // Debug logs to check what we're getting
  console.log("All tariffs from useTariffs hook:", tariffs);
  console.log("Valid tariffs after filtering:", validTariffs);
  console.log(
    "Booking types in tariffs:",
    tariffs.map((t) => ({ id: t.id, bookings_type: t.bookings_type }))
  );

  // Filter out archived bookings
  const activeBookings = bookings.filter(
    (booking) => booking.status !== "archived"
  );

  // Group tariffs by transport type
  const airTariffs =
    validTariffs.filter((t) => t?.transport_type?.id === 1) || [];
  const oceanTariffs =
    validTariffs.filter((t) => t?.transport_type?.id === 2) || [];

  // Filter the tariffs to handle special cases for display
  const getFilteredTariffs = (
    tariffs: Tariff[],
    selectedRoute: string | null
  ): Tariff[] => {
    if (!selectedRoute) return [];

    return tariffs.filter((tariff) => {
      // First filter by route
      const route = `${tariff.origin_port?.name || "Unknown"}${
        tariff.origin_port?.country ? ` (${tariff.origin_port.country})` : ""
      } → ${tariff.destination_port?.name || "Unknown"}${
        tariff.destination_port?.country
          ? ` (${tariff.destination_port.country})`
          : ""
      }`;

      if (route !== selectedRoute) return false;

      // For LCL (bookings_type ID 1), ensure tariff_bracket is not null
      if (tariff.bookings_type?.id === 1 && !tariff.tariff_bracket) {
        return false;
      }

      // For FCL (bookings_type ID 2), container_size should not be null
      if (tariff.bookings_type?.id === 2 && !tariff.container_size) {
        return false;
      }

      return true;
    });
  };

  // Get unique routes for air tariffs
  const airRoutes = [
    ...new Set(
      airTariffs.map(
        (t) =>
          `${t.origin_port?.name || "Unknown"}${
            t.origin_port?.country ? ` (${t.origin_port.country})` : ""
          } → ${t.destination_port?.name || "Unknown"}${
            t.destination_port?.country
              ? ` (${t.destination_port.country})`
              : ""
          }`
      )
    ),
  ];

  // Get unique routes for ocean tariffs
  const oceanRoutes = [
    ...new Set(
      oceanTariffs.map(
        (t) =>
          `${t.origin_port?.name || "Unknown"}${
            t.origin_port?.country ? ` (${t.origin_port.country})` : ""
          } → ${t.destination_port?.name || "Unknown"}${
            t.destination_port?.country
              ? ` (${t.destination_port.country})`
              : ""
          }`
      )
    ),
  ];

  // Set default selected routes if not already set
  useEffect(() => {
    if (airRoutes.length > 0 && !selectedAirRoute) {
      setSelectedAirRoute(airRoutes[0]);
    }
    if (oceanRoutes.length > 0 && !selectedOceanRoute) {
      setSelectedOceanRoute(oceanRoutes[0]);
    }
  }, [airRoutes, oceanRoutes, selectedAirRoute, selectedOceanRoute]);

  // Show loading spinner only while checking for company
  if (hasCompany === null) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  // Show create company message if no company exists
  if (!hasCompany) {
    return (
      <DashboardContainer>
        <Result
          icon={<BankOutlined style={{ fontSize: 48, color: "#1890ff" }} />}
          status="info"
          title={t("companyInfo.noCompanyFound")}
          subTitle={t("companyInfo.createCompanyPrompt")}
          extra={[
            <Button
              type="primary"
              key="create"
              onClick={() => navigate("/company-info")}
            >
              {t("companyInfo.createCompany")}
            </Button>,
          ]}
        />
      </DashboardContainer>
    );
  }

  // Show loading spinner while loading dashboard data
  if (loadingBookings || loadingTariffs) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  // Prepare tariff data for display
  const prepareTariffData = (
    tariffs: Tariff[],
    isAirFreight: boolean
  ): TariffDisplayItem[] => {
    // Sort tariffs by shipment type and then by tariff bracket
    const sortedTariffs = [...tariffs].sort((a, b) => {
      // First sort by shipment type
      const aType = a.bookings_type?.name?.toLowerCase() || "";
      const bType = b.bookings_type?.name?.toLowerCase() || "";

      if (aType < bType) return -1;
      if (aType > bType) return 1;

      // Then sort by tariff bracket in ascending order by "from" value
      if (a.tariff_bracket && b.tariff_bracket) {
        return (a.tariff_bracket.from || 0) - (b.tariff_bracket.from || 0);
      }

      return 0;
    });

    return sortedTariffs.map((tariff) => {
      // Format bracket information
      const bracketInfo =
        tariff.bookings_type?.id === 2
          ? "" // Don't show bracket info for FCL (container) shipments
          : tariff.tariff_bracket
          ? tariff.tariff_bracket.to === 999
            ? ` (${tariff.tariff_bracket.from || 0}+ ${
                isAirFreight ? "kg" : "cbm"
              })`
            : ` (${tariff.tariff_bracket.from || 0} - ${
                tariff.tariff_bracket.to ? tariff.tariff_bracket.to : "∞"
              } ${isAirFreight ? "kg" : "cbm"})`
          : "";

      // Add container size information if available
      const containerSizeInfo = tariff.container_size
        ? ` (${tariff.container_size.name})`
        : "";

      // Determine the unit based on transport type and booking type
      let priceUnit = "per kg"; // Default for airfreight

      if (!isAirFreight) {
        // For seafreight (transport_type ID 2)
        if (tariff.transport_type?.id === 2) {
          if (tariff.bookings_type?.id === 1) {
            // LCL (ID 1) should show "per cbm"
            priceUnit = "per cbm";
          } else if (tariff.container_size || tariff.bookings_type?.id === 2) {
            // FCL (ID 2) or when container size is selected
            priceUnit = "per container";
          } else {
            // Default for seafreight
            priceUnit = "per cbm";
          }
        } else {
          // Default for non-airfreight, non-seafreight
          priceUnit = "per cbm";
        }
      }

      return {
        key: tariff.id,
        price: `€${tariff.price} ${priceUnit}${bracketInfo}${containerSizeInfo}`,
        shipmentType: tariff.bookings_type?.name || "",
        validity: (
          <ValidityTag isActive={!tariff.to || dayjs(tariff.to).isAfter(today)}>
            {tariff.from && dayjs(tariff.from).format("DD MMM YYYY")} -{" "}
            {tariff.to
              ? dayjs(tariff.to).format("DD MMM YYYY")
              : t("common.unlimited")}
          </ValidityTag>
        ),
        isActive: !tariff.to || dayjs(tariff.to).isAfter(today),
        isFuture: Boolean(tariff.from && dayjs(tariff.from).isAfter(today)),
        fromDate: tariff.from ? dayjs(tariff.from).valueOf() : 0,
      };
    });
  };

  const airTariffData = selectedAirRoute
    ? prepareTariffData(getFilteredTariffs(airTariffs, selectedAirRoute), true)
    : [];

  const oceanTariffData = selectedOceanRoute
    ? prepareTariffData(
        getFilteredTariffs(oceanTariffs, selectedOceanRoute),
        false
      )
    : [];

  const getStatusCount = (status: string) => {
    if (status === "archived") {
      return bookings.filter((booking) => booking.status === status).length;
    }
    return activeBookings.filter((booking) => booking.status === status).length;
  };

  const getTransportTypeStats = () => {
    const stats = activeBookings.reduce((acc, booking) => {
      const type = booking.transport_type?.name || "Unknown";
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const total = Object.values(stats).reduce((sum, count) => sum + count, 0);
    return Object.entries(stats).map(([type, count]) => ({
      type,
      value: count,
      percent: total > 0 ? Math.round((count / total) * 100) : 0,
    }));
  };

  interface RecentBooking {
    id: number;
    key: number;
    reference: string;
    status: string;
    transport: string;
    isEditable: boolean;
    canArchive: boolean;
    transport_type: {
      name: string;
      abbreviation: string;
    };
    rejection_reason?: string | null;
    track_and_trace?: string | null;
  }

  const recentBookings: RecentBooking[] = activeBookings
    .slice(0, 5)
    .map((booking) => ({
      key: booking.id,
      id: booking.id,
      reference: booking.reference,
      status: booking.status,
      transport: booking.transport_type?.name || "Unknown",
      transport_type: booking.transport_type || {
        name: "Unknown",
        abbreviation: "",
      },
      isEditable: booking.status === "concept" || booking.status === "rejected",
      canArchive: [
        "delivered",
        "added_to_stock",
        "cancelled",
        "rejected",
        "concept",
        "submitted",
      ].includes(booking.status),
      rejection_reason: booking.rejection_reason,
      track_and_trace: (booking as { track_and_trace?: string | null })
        .track_and_trace,
    }));

  const renderTariffContent = (
    tariffData: TariffDisplayItem[],
    routes: string[],
    selectedRoute: string | null,
    setSelectedRoute: (value: string) => void
  ) => {
    return (
      <>
        <RouteSelect
          value={selectedRoute}
          onChange={(value: unknown) => {
            if (typeof value === "string") {
              setSelectedRoute(value);
            }
          }}
          options={routes.map((route) => ({ value: route, label: route }))}
          placeholder={t("dashboard.selectRoute")}
        />
        {selectedRoute ? (
          tariffData.length > 0 ? (
            <Table dataSource={tariffData} pagination={false} size="small">
              <Table.Column title={t("dashboard.price")} dataIndex="price" />
              <Table.Column
                title={t("dashboard.validityPeriod")}
                dataIndex="validity"
              />
            </Table>
          ) : (
            <Empty description={t("dashboard.noRatesAvailable")} />
          )
        ) : (
          <Empty description={t("dashboard.noRatesForRoute")} />
        )}
      </>
    );
  };

  // Function to handle booking archiving
  const handleArchive = async (id: number) => {
    try {
      await archiveBooking(id);
      message.success(t("bookings.archivedSuccess"));
    } catch (err) {
      console.error("Error archiving booking:", err);
      message.error(t("common.error"));
    }
  };

  const handleClick = (bookingId: number, isEditable: boolean) => {
    navigate(`/bookings/${bookingId}/${isEditable ? "edit" : "view"}`);
  };

  // Function to get recent bookings for a specific status
  const getRecentStatusBookings = (status: string): StatusBooking[] => {
    const bookingsToUse = status === "archived" ? bookings : activeBookings;
    return bookingsToUse
      .filter((booking) => booking.status === status)
      .slice(0, 5)
      .map((booking) => ({
        id: booking.id,
        reference: booking.reference,
        transport_type: booking.transport_type?.name || "Unknown",
        eta: booking.eta,
        isEditable:
          booking.status === "concept" || booking.status === "rejected",
      }));
  };

  // Helper function to ensure status translations are strings
  const safeTranslate = (key: string, fallback: string): string => {
    const translation = t(key);
    return typeof translation === "string" ? translation : fallback;
  };

  return (
    <DashboardContainer>
      {!hasCompany && (
        <div
          style={{
            marginBottom: "24px",
            padding: "24px",
            background: "#fff",
            borderRadius: "8px",
            boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
          }}
        >
          <Result
            icon={<BankOutlined style={{ fontSize: 48, color: "#1890ff" }} />}
            status="info"
            title={t("companyInfo.noCompanyFound")}
            subTitle={t("companyInfo.createCompanyPrompt")}
            extra={[
              <Button
                type="primary"
                key="create"
                onClick={() => navigate("/company-info")}
              >
                {t("companyInfo.createCompany")}
              </Button>,
            ]}
          />
        </div>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <h1>{t("dashboard.title")}</h1>
        <Tooltip
          title={
            companyDetails?.status !== "approved"
              ? t(
                  "companyInfo.companyNotApproved",
                  "Your company needs to be approved before creating bookings"
                )
              : ""
          }
        >
          <Button
            type="primary"
            icon={<FileTextOutlined />}
            onClick={() => {
              if (companyDetails?.status === "approved") {
                navigate("/bookings/new");
              }
            }}
            disabled={companyDetails?.status !== "approved"}
          >
            {t("dashboard.createNewBooking", "Create New Booking")}
          </Button>
        </Tooltip>
      </div>

      <StatsContainer className={isExpanded ? "expanded" : ""}>
        <div className="stats-section">
          <StyledCard onClick={() => navigate("/bookings")}>
            <Statistic
              title={t("dashboard.totalBookingsTitle")}
              value={activeBookings.length}
              prefix={<FileTextOutlined style={{ fontSize: 20 }} />}
              valueStyle={{ color: "#1890ff" }}
            />
          </StyledCard>
          {["rejected", "cancelled", "archived"].map((status) => (
            <StyledCard
              key={status}
              isStatusCard
              onClick={() => {
                if (getStatusCount(status) > 0) {
                  setSelectedStatus(status);
                  setIsModalVisible(true);
                } else {
                  navigate(`/bookings?tab=${status}`);
                }
              }}
            >
              <Statistic
                title={safeTranslate(
                  `status.${status}`,
                  status.replace(/_/g, " ")
                )}
                value={getStatusCount(status)}
                prefix={statusIcons[status]}
                valueStyle={{ color: statusColors[status] }}
              />
            </StyledCard>
          ))}
        </div>

        <div className="divider" />

        <div>
          <h3 style={{ margin: "0 0 16px 0" }}>{t("dashboard.perStatus")}</h3>
          <div className="stats-section">
            {[
              "concept",
              "submitted",
              "approved",
              "waiting_for_goods",
              "goods_received_origin",
              "in_transit",
              "custom_clearance",
              "out_for_delivery",
              "delivered",
              "added_to_stock",
            ].map((status) => (
              <StyledCard
                key={status}
                isStatusCard
                onClick={() => {
                  if (getStatusCount(status) > 0) {
                    setSelectedStatus(status);
                    setIsModalVisible(true);
                  } else {
                    navigate(`/bookings?tab=${status}`);
                  }
                }}
              >
                <Statistic
                  title={safeTranslate(
                    `status.${status}`,
                    status.replace(/_/g, " ")
                  )}
                  value={getStatusCount(status)}
                  prefix={statusIcons[status]}
                  valueStyle={{ color: statusColors[status] }}
                />
              </StyledCard>
            ))}
          </div>
        </div>
      </StatsContainer>

      <ShowMoreButton type="dashed" onClick={() => setIsExpanded(!isExpanded)}>
        {isExpanded
          ? t("dashboard.showLessStatuses")
          : t("dashboard.showMoreStatuses")}
      </ShowMoreButton>

      <Modal
        title={
          selectedStatus
            ? safeTranslate(
                `status.${selectedStatus}`,
                selectedStatus.replace(/_/g, " ")
              )
            : ""
        }
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={
          selectedStatus && (
            <Button
              type="primary"
              onClick={() => {
                setIsModalVisible(false);
                navigate(`/bookings?tab=${selectedStatus}`);
              }}
            >
              {t("dashboard.viewAllStatusBookings", {
                status: safeTranslate(
                  `status.${selectedStatus}`,
                  selectedStatus.replace(/_/g, " ")
                ).toLowerCase(),
                count: getStatusCount(selectedStatus),
              })}
            </Button>
          )
        }
        width={600}
      >
        {selectedStatus && (
          <Table<StatusBooking>
            dataSource={getRecentStatusBookings(selectedStatus)}
            pagination={false}
            size="small"
          >
            <Table.Column
              title={t("bookings.bookingId")}
              dataIndex="id"
              key="id"
              render={(id) => <span>EFF{id}</span>}
            />
            <Table.Column
              title={t("bookings.reference")}
              dataIndex="reference"
              key="reference"
              render={(text) => text || "-"}
            />
            <Table.Column
              title={t("bookings.transportType")}
              dataIndex="transport_type"
              key="transport_type"
            />
            <Table.Column
              title={t("bookings.eta")}
              dataIndex="eta"
              key="eta"
              render={(date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-")}
            />
            <Table.Column
              title={t("common.actions")}
              key="action"
              render={(_, record: StatusBooking) => (
                <Button
                  type="primary"
                  icon={<EyeOutlined />}
                  onClick={() => {
                    setIsModalVisible(false);
                    navigate(
                      `/bookings/${record.id}/${
                        record.isEditable ? "edit" : "view"
                      }`
                    );
                  }}
                />
              )}
            />
          </Table>
        )}
      </Modal>

      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={12}>
          <TariffCard title={t("dashboard.airFreightRatesTitle")}>
            {renderTariffContent(
              airTariffData,
              airRoutes,
              selectedAirRoute,
              setSelectedAirRoute
            )}
          </TariffCard>
        </Col>
        <Col xs={24} lg={12}>
          <TariffCard title={t("dashboard.oceanFreightRatesTitle")}>
            {renderTariffContent(
              oceanTariffData,
              oceanRoutes,
              selectedOceanRoute,
              setSelectedOceanRoute
            )}
          </TariffCard>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={12}>
          <StyledCard title={t("dashboard.transportTypesTitle")}>
            {getTransportTypeStats().map(({ type, value, percent }) => (
              <div key={type} style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 4 }}>
                  <span style={{ marginRight: 8 }}>
                    {type === "Airfreight"
                      ? t("dashboard.airfreight")
                      : type === "Seafreight"
                      ? t("dashboard.seafreight")
                      : type}
                  </span>
                  <span style={{ float: "right" }}>{value}</span>
                </div>
                <Progress percent={percent} showInfo={false} />
              </div>
            ))}
          </StyledCard>
        </Col>
        <Col xs={24} lg={12}>
          <StyledCard title={t("dashboard.recentBookingsTitle")}>
            {/* Desktop view */}
            <DesktopView>
              <Table
                dataSource={recentBookings}
                pagination={false}
                size="small"
              >
                <Table.Column
                  title={t("bookings.bookingId")}
                  dataIndex="id"
                  key="id"
                  render={(id) => <span>EFF{id}</span>}
                />
                <Table.Column
                  title={t("bookings.reference")}
                  dataIndex="reference"
                  key="reference"
                  render={(text) => text || "-"}
                />
                <Table.Column
                  title={t("common.status", "Status")}
                  dataIndex="status"
                  key="status"
                  render={(status, record: RecentBooking) => {
                    const normalizedStatus = status.replace(/-/g, "_");

                    if (status === "rejected" && record.rejection_reason) {
                      return (
                        <Tooltip title={record.rejection_reason}>
                          <Tag color={statusColors[status] || "default"}>
                            {safeTranslate(
                              `status.${normalizedStatus}`,
                              normalizedStatus.replace(/_/g, " ")
                            )}{" "}
                            <ExclamationCircleOutlined />
                          </Tag>
                        </Tooltip>
                      );
                    }

                    return (
                      <Tag color={statusColors[status] || "default"}>
                        {safeTranslate(
                          `status.${normalizedStatus}`,
                          normalizedStatus.replace(/_/g, " ")
                        )}
                      </Tag>
                    );
                  }}
                />
                <Table.Column
                  title={t("common.actions")}
                  key="action"
                  render={(_, booking: RecentBooking) => (
                    <Space size="small">
                      <Button
                        type="primary"
                        icon={<EyeOutlined />}
                        onClick={() =>
                          handleClick(booking.id, booking.isEditable)
                        }
                      />
                      {booking.canArchive && (
                        <Popconfirm
                          title={t("bookings.confirmArchive")}
                          onConfirm={() =>
                            booking.id && handleArchive(booking.id)
                          }
                          okText={t("common.yes")}
                          cancelText={t("common.no")}
                        >
                          <Button
                            type="text"
                            icon={<ArchiveOutlined />}
                            danger
                          />
                        </Popconfirm>
                      )}
                    </Space>
                  )}
                />
              </Table>
            </DesktopView>

            {/* Mobile view */}
            <MobileView>
              {recentBookings.map((booking) => (
                <Card
                  key={booking.id}
                  size="small"
                  style={{ marginBottom: 8 }}
                  onClick={() => handleClick(booking.id, booking.isEditable)}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 8,
                    }}
                  >
                    <span style={{ fontWeight: "bold" }}>EFF{booking.id}</span>
                    <Tag color={statusColors[booking.status] || "default"}>
                      {safeTranslate(
                        `status.${booking.status}`,
                        booking.status.replace(/_/g, " ")
                      )}
                      {booking.status === "rejected" &&
                        booking.rejection_reason && (
                          <ExclamationCircleOutlined
                            style={{ marginLeft: 4 }}
                          />
                        )}
                    </Tag>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 8,
                    }}
                  >
                    <span style={{ color: "rgba(0, 0, 0, 0.45)" }}>
                      {t("bookings.reference")}:
                    </span>
                    <span>{booking.reference || "-"}</span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 8,
                      marginTop: 12,
                    }}
                  >
                    {booking.track_and_trace && (
                      <Button
                        size="small"
                        icon={<EnvironmentOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (booking.track_and_trace) {
                            window.open(booking.track_and_trace, "_blank");
                          }
                        }}
                      />
                    )}
                    {booking.canArchive && (
                      <Popconfirm
                        title={t("bookings.confirmArchive")}
                        onConfirm={(e?: React.MouseEvent) => {
                          if (e) {
                            e.stopPropagation();
                          }
                          handleArchive(booking.id);
                        }}
                        okText={t("common.yes")}
                        cancelText={t("common.no")}
                      >
                        <Button
                          danger
                          size="small"
                          icon={<ArchiveOutlined />}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Popconfirm>
                    )}
                  </div>
                </Card>
              ))}
            </MobileView>

            <div style={{ marginTop: 16, textAlign: "right" }}>
              <Button type="primary" onClick={() => navigate("/bookings")}>
                {t("dashboard.viewAllBookings")}
              </Button>
            </div>
          </StyledCard>
        </Col>
      </Row>
    </DashboardContainer>
  );
};
